import { useEffect, useRef } from 'react';

interface ChatComposerWaveformProps {
  analyser: AnalyserNode | null;
  paused?: boolean;
}

const BAR_WIDTH = 2;
const BAR_GAP = 2;
const MIN_BAR_HEIGHT = 2;
const LEVEL_BOOST = 6;

export function ChatComposerWaveform({ analyser, paused = false }: ChatComposerWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const levelsRef = useRef<number[]>([]);
  // A paused recorder keeps the microphone open, so the analyser still hears the room
  // while nothing is being kept. Flat bars are the honest signal.
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analyser) {
      return;
    }
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }
    const samples = new Uint8Array(analyser.fftSize);
    let frameId = 0;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
      }

      analyser.getByteTimeDomainData(samples);
      let sumSquares = 0;
      for (let i = 0; i < samples.length; i += 1) {
        const deviation = ((samples[i] ?? 128) - 128) / 128;
        sumSquares += deviation * deviation;
      }
      const rms = Math.sqrt(sumSquares / samples.length);
      const level = pausedRef.current ? 0 : Math.min(1, rms * LEVEL_BOOST);

      const barCount = Math.max(1, Math.floor(width / (BAR_WIDTH + BAR_GAP)));
      const levels = levelsRef.current;
      levels.push(level);
      while (levels.length > barCount) {
        levels.shift();
      }

      context.clearRect(0, 0, width, height);
      const style = window.getComputedStyle(canvas);
      context.fillStyle = style.color;
      context.globalAlpha = pausedRef.current ? 0.4 : 1;
      for (let i = 0; i < levels.length; i += 1) {
        const barHeight = Math.max(MIN_BAR_HEIGHT, (levels[i] ?? 0) * height);
        const x = width - (levels.length - i) * (BAR_WIDTH + BAR_GAP);
        const y = (height - barHeight) / 2;
        context.fillRect(x, y, BAR_WIDTH, barHeight);
      }

      frameId = window.requestAnimationFrame(draw);
    };

    frameId = window.requestAnimationFrame(draw);
    return () => {
      window.cancelAnimationFrame(frameId);
      levelsRef.current = [];
    };
  }, [analyser]);

  return (
    <canvas
      data-testid="chat-composer-waveform"
      ref={canvasRef}
      className="h-7 w-full text-muted-foreground"
    />
  );
}
